import axios from 'axios';
import Cookies from 'js-cookie';
import { storageKeys } from '../constants/storageKeys';
import initialSettings from '../constants/settings.json';
import { generateCustomizedSchema } from "./generateCustomizedSchema";


/**
 * Fetches a response from the LLM (Large Language Model) API, either via a session key (proxy) or directly using an API key.
 * 
 * This function constructs the appropriate message payload, determines the LLM model and temperature from localStorage,
 * and sends a request to the LLM endpoint. It supports both direct API calls and proxy-based session key calls.
 * The function also handles function calling for BPMN meta model schems.
 * 
 * @param {Object} history - The conversation history object.
 * @param {string} prompt - The user prompt to send to the LLM.
 * @param {string|null} instructions - Optional system instructions to override defaults.
 * @param {AbortSignal} [signal] - Optional abort controller signal to enable request cancellation.
 * @returns {Promise<Object>} - The processed LLM response containing the extracted model.
 * @throws {Error} - Throws if the request fails or if no valid JSON data is found in the response.
 */
export const generateLLMResponse = async (history, prompt, instructions = null, signal = undefined) => {
  try {
    const llmJsonSchema = JSON.parse(localStorage.getItem(storageKeys.LLM_JSON_SCHEMA) ?? initialSettings.localStorage.LLM_JSON_SCHEMA);
    const messages = createMessages(history, prompt, instructions, llmJsonSchema);
    const selectedLLM = JSON.parse(localStorage.getItem(storageKeys.LLM_MODEL) ?? initialSettings.localStorage.LLM_MODEL);
    const temperature = JSON.parse(localStorage.getItem(storageKeys.LLM_RESPONSE_TEMPERATURE) ?? initialSettings.localStorage.LLM_RESPONSE_TEMPERATURE);
    const useToolsCalls = JSON.parse(localStorage.getItem(storageKeys.USE_TOOLS_CALLS) ?? initialSettings.localStorage.USE_TOOLS_CALLS);
    const additionalLLMOptions = JSON.parse(localStorage.getItem(storageKeys.ADDITIONAL_LLM_OPTIONS) ?? initialSettings.localStorage.ADDITIONAL_LLM_OPTIONS);

    let additionalOptions = {};
    if (useToolsCalls) {
      additionalOptions = {
        tools: [
          {    
            type: "function",
            function: {
              name: "bpmn_meta_model",
              description: "Describe a business process model",
              parameters: llmJsonSchema? llmJsonSchema : generateCustomizedSchema()
            }
          }
        ],
        tool_choice: {
          type: "function",
          function: {
            name: "bpmn_meta_model",
          }
        }
      };
    }

    const apiKey = Cookies.get('apiKey');
    const sessionKey = Cookies.get('sessionKey');
    const baseURL = (JSON.parse(localStorage.getItem(storageKeys.LLM_API_URL) ?? initialSettings.localStorage.LLM_API_URL)).trim();

    // Conditionally handle direct API or proxy request based on baseURL
    let response;
    if (sessionKey) {
      try {
        // Validate sessionKey
        response = await axios.post(
          `${process.env.REACT_APP_PROXY}/session/chat/completions`,
          {
            sessionKey,
            body: {
              model: selectedLLM,
              ...(typeof temperature === "number" ? { temperature } : {}),
              ...additionalLLMOptions,
              messages,
              ...additionalOptions
            }
          },
          signal ? { signal } : undefined
        );
      } catch (error) {
        throw new Error(error?.response?.data?.error || 'Session key validation failed.');
      }
    } else if (apiKey) {
      // Create an Axios instance with default settings
      const instance = axios.create({
        baseURL,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
      });
      response = await instance.post(
        '/chat/completions',
        {
          model: selectedLLM,
          ...(typeof temperature === "number" ? { temperature } : {}),
          ...additionalLLMOptions,
          messages,
          ...additionalOptions
        },
        signal ? { signal } : undefined
      );
    } else {
      throw new Error('No API key or session key provided.');
    }

    let jsonData;
    if (!useToolsCalls) {
      // Extract JSON from markdown code block in the response string
      // The model is a string containing markdown with a JSON code block
      const modelStr = response.data.choices[0]?.message?.content || '';
      const match = modelStr.match(/```json\s*([\s\S]*?)\s*```/i);
      if (match && match[1]) {
        jsonData = match[1].trim();
      } else {
        // fallback: try to extract any JSON-looking substring
        const fallbackMatch = modelStr.match(/{[\s\S]*}/);
        jsonData = fallbackMatch ? fallbackMatch[0] : modelStr;
      }
    } else {
      jsonData = response.data.choices[0]?.message?.tool_calls[0].function.arguments;
    }

    if (jsonData) {
      response.data.model = jsonData;
      delete response.data.choices;
      return response.data;
    } else {
      throw new Error('No JSON data found in the LLM response.');
    }

  } catch (error) {
    throw error;
  }
};


/**
 * Constructs an array of message objects for LLM chat completion.
 * 
 * This function builds the message history to send to the LLM, including:
 *   - A system message (from provided instructions or localStorage)
 *   - An optional user instructions message (from localStorage if no instructions are provided)
 *   - The previous assistant response from history (if available)
 *   - The current user prompt
 * 
 * @param {Object} history - The chat history object, containing entries and a pointer.
 * @param {string} prompt - The current user prompt to add to the messages.
 * @param {string|null} instructions - Optional system instructions to override localStorage.
 * @returns {Array} - An array of message objects for the LLM API.
 */
const createMessages = (history, prompt, instructions = null, llmJsonSchema = null) =>  {
    const messages = [];
    // Get the system message and context from localStorage and trim any extra whitespace
    // Use the provided systemInstructions or fallback to localStorage
    const systemInstructions = instructions 
        ? instructions.trim() 
        : (JSON.parse(localStorage.getItem(storageKeys.LLM_SYSTEM_INSTRUCTIONS)) || '').trim();

    const userInstructions = instructions 
        ? ''
        : (JSON.parse(localStorage.getItem(storageKeys.LLM_USER_INSTRUCTIONS)) || '').trim();
    
    // Add the system message only if it exists after trimming
    if (systemInstructions) {
        messages.push({
            role: "system",
            content: systemInstructions
        });
    }

    if (userInstructions) {
      messages.push({
          role: "user",
          content: userInstructions
      });
    }
    
    // If history exists, add assistant's previous response after the system message
    const currentHistoryEntry = history.entries.length > 0 ? history.entries[history.pointer] : null;
    if (currentHistoryEntry) {
      messages.push({
        "role": "assistant",
        "content": currentHistoryEntry.jsonData
      });
    }

    // Add user's prompt message to the messages array
    messages.push({
      "role": "user",
      "content": prompt
    });

    // If UseToolsCalls is false, add the schema as a user message
    const useToolsCalls = JSON.parse(localStorage.getItem(storageKeys.USE_TOOLS_CALLS) ?? initialSettings.localStorage.USE_TOOLS_CALLS);
    if (!useToolsCalls) {
      const schema = llmJsonSchema? llmJsonSchema : generateCustomizedSchema();
      if (schema) {
        messages.push({
          role: "user",
          content: `Here is the schema to use. Please provide a process using this schema, 
          and format your output as markdown with a JSON code block, like so: 
          \`\`\`json\n{...}\n\`\`\`.\n\nSchema:\n${schema}`
        });
      }
    }
    return messages;
};